/**
 * Error Handling Utility Functions
 *
 * Centralized utilities for handling errors across the application.
 * Provides consistent error state management with Redux and sessionStorage fallback.
 *
 * @module utils/errorHandler
 */

import { uiActions } from 'store/modules/ui-slice';

/**
 * Sets error state in both Redux and sessionStorage for consistent error handling
 *
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} navigate - React Router navigate function
 * @param {Object} location - React Router location object
 * @param {Object} errorInfo - Error information object with status and data
 * @param {Object} entityConfig - Entity configuration for ErrorDisplay component
 * @param {string} entityConfig.name - Entity name (e.g., "Course", "Class", "Quest")
 * @param {string} entityConfig.homeRoute - Route to navigate back to
 *
 * @example
 * // In a component with error handling
 * try {
 *   const response = await axios.get(`/courses/${id}`);
 *   setCourse(response.data);
 * } catch (error) {
 *   const errorInfo = {
 *     status: error.response?.status || 500,
 *     data: error.response?.data || {
 *       title: 'Error Loading Course',
 *       detail: 'An unexpected error occurred while loading the course.',
 *     },
 *   };
 *
 *   const entityConfig = {
 *     name: 'Course',
 *     homeRoute: '/',
 *   };
 *
 *   handleErrorAndNavigate(dispatch, navigate, location, errorInfo, entityConfig);
 * }
 */
export const handleErrorAndNavigate = (
  dispatch,
  navigate,
  location,
  errorInfo,
  entityConfig
) => {
  // Get current URL for "Try Again" functionality
  const previousUrl = `${location.pathname}${location.search}${location.hash}`;

  // Create serializable entityConfig (exclude functions)
  const serializableEntityConfig = {
    name: entityConfig.name,
    homeRoute: entityConfig.homeRoute,
    courseId: entityConfig.courseId, // Include courseId for enhanced class access links
    // Note: getAccessRoute function is not stored in Redux to maintain serializability
    // The ErrorDisplay component will handle class access routes generically
  };

  // Create error state object
  const errorState = {
    error: errorInfo,
    entityConfig: serializableEntityConfig,
    previousUrl,
  };

  // Set error in Redux store
  dispatch(uiActions.setError(errorState));

  // Also store in sessionStorage as backup for page refreshes
  sessionStorage.setItem('errorState', JSON.stringify(errorState));

  // Navigate to error page
  navigate('/error', { replace: true });
};

/**
 * Creates a standardized error info object from an error response
 *
 * @param {Object} error - The error object from API call
 * @param {string} defaultTitle - Default title if not provided in error
 * @param {string} defaultDetail - Default detail message if not provided in error
 * @returns {Object} Standardized error info object
 *
 * @example
 * // Handle axios error
 * const errorInfo = createErrorInfo(
 *   error,
 *   'Course Not Found',
 *   'The course you are looking for does not exist or may have been removed.'
 * );
 */
export const createErrorInfo = (error, defaultTitle, defaultDetail) => {
  return {
    status: error.response?.status || error.status || 500,
    data: error.response?.data ||
      error.data || {
        title: defaultTitle,
        detail: defaultDetail,
      },
  };
};

/**
 * Creates a standardized error info object for Redux action failures
 *
 * @param {Object} actionResult - The rejected action result from Redux Toolkit
 * @param {string} defaultTitle - Default title if not provided in error
 * @param {string} defaultDetail - Default detail message if not provided in error
 * @returns {Object} Standardized error info object
 *
 * @example
 * // Handle Redux action failure
 * if (fetchCourseByIdAction.rejected.match(result)) {
 *   const errorInfo = createErrorInfoFromAction(
 *     result,
 *     'Error Loading Course',
 *     'An unexpected error occurred while loading the course.'
 *   );
 * }
 */
export const createErrorInfoFromAction = (
  actionResult,
  defaultTitle,
  defaultDetail
) => {
  const errorPayload = actionResult.payload;

  return {
    status: errorPayload?.status || 500,
    data: errorPayload?.data || {
      title: defaultTitle,
      detail: defaultDetail,
    },
  };
};
